// KLHS page helpers (contact, quotation and guide sections).

// <klhs-open-status>: "Open now" / "Closed · opens tomorrow 9am", using the hours set on the KLHS WhatsApp chat
// section, so business hours live in one place.
if (!customElements.get('klhs-open-status')) {
  class KlhsOpenStatus extends HTMLElement {
    connectedCallback() {
      const chat = document.querySelector('klhs-chat');
      if (!chat) return;
      customElements.whenDefined('klhs-chat').then(() => {
        this.update(chat);
        this.timer = setInterval(() => this.update(chat), 60000);
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    update(chat) {
      const { afterHours, next } = chat.getHoursState();
      this.classList.toggle('klhs-open-status--closed', afterHours);
      const template = afterHours ? this.dataset.closedText : this.dataset.openText;
      this.querySelector('[data-status-text]').textContent = (template || '').replace('{time}', next || '').trim();
      this.hidden = false;
    }
  }

  customElements.define('klhs-open-status', KlhsOpenStatus);
}

// <klhs-toc>: builds an "In this guide" list from the h2 headings of the target content.
if (!customElements.get('klhs-toc')) {
  class KlhsToc extends HTMLElement {
    connectedCallback() {
      const content = document.getElementById(this.dataset.target);
      const list = this.querySelector('[data-toc-list]');
      if (!content || !list) return;

      const headings = [...content.querySelectorAll('h2')];
      if (headings.length < 2) {
        this.hidden = true;
        return;
      }

      const used = new Set();
      headings.forEach((heading) => {
        if (!heading.id) {
          let id = heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'section';
          while (used.has(id) || document.getElementById(id)) id += '-1';
          heading.id = id;
        }
        used.add(heading.id);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);
      });
      this.hidden = false;

      // Start collapsed on small screens, where the list sits above the guide.
      if (window.matchMedia('(max-width: 989px)').matches) this.querySelector('details')?.removeAttribute('open');

      // Highlight the section being read.
      const links = [...list.querySelectorAll('a')];
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((link) => link.toggleAttribute('aria-current', link.hash === `#${entry.target.id}`));
          });
        },
        { rootMargin: '0px 0px -70% 0px' }
      );
      headings.forEach((heading) => observer.observe(heading));
    }
  }

  customElements.define('klhs-toc', KlhsToc);
}

// <klhs-steps>: on small screens, shows a long form one fieldset at a time with Next / Back.
// On larger screens every step stays visible and the controls are hidden.
if (!customElements.get('klhs-steps')) {
  class KlhsSteps extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.media = window.matchMedia('(max-width: 749px)');
      this.current = 0;

      this.querySelectorAll('[data-step-next]').forEach((button) =>
        button.addEventListener('click', () => this.go(this.current + 1, true))
      );
      this.querySelectorAll('[data-step-back]').forEach((button) =>
        button.addEventListener('click', () => this.go(this.current - 1, false))
      );
      this.querySelectorAll('[data-quote-whatsapp]').forEach((button) =>
        button.addEventListener('click', () => this.sendOnWhatsApp(button))
      );
      this.onMedia = () => this.render();
      this.media.addEventListener('change', this.onMedia);

      // If the server returned errors, open the first step that has an invalid field.
      const invalid = this.querySelector('[aria-invalid="true"]');
      if (invalid) this.current = Math.max(0, this.steps.indexOf(invalid.closest('[data-step]')));
      this.render();
    }

    disconnectedCallback() {
      this.media?.removeEventListener('change', this.onMedia);
    }

    // Builds a WhatsApp message from whatever the customer has typed, so the quote form is not email-only.
    sendOnWhatsApp(button) {
      const form = this.closest('form');
      const field = (name) => form.elements[name];
      const value = (name) => (field(name)?.value || '').trim();

      // Organisation and items are the minimum a specialist needs; send the customer to the step that is missing one.
      const missing = ['contact[Organisation]', 'contact[Items and quantities]'].find((name) => !value(name));
      if (missing) {
        const step = field(missing).closest('[data-step]');
        if (this.media.matches) this.go(this.steps.indexOf(step), false);
        field(missing).reportValidity();
        return;
      }

      const needs = [...form.querySelectorAll('input[type="checkbox"][name^="contact[Needs: "]:checked')].map((box) =>
        box.name.replace('contact[Needs: ', '').replace(/\]$/, '')
      );
      const type = form.querySelector('input[name="contact[Organisation type]"]:checked')?.value;
      const rows = [
        ['Organisation type', type],
        ['Organisation', value('contact[Organisation]')],
        ['Name & role', value('contact[name]')],
        ['Email', value('contact[email]')],
        ['Phone / WhatsApp', value('contact[Phone]')],
        ['Needs', needs.join(', ')],
        ['Items and quantities', value('contact[Items and quantities]')],
        ['Deliver to', value('contact[Deliver to]')],
        ['Town / state', value('contact[Town / state]')],
        ['Needed by', value('contact[Needed by]')],
        ['Paperwork', value('contact[Paperwork]')],
        ['Anything else', value('contact[body]')],
      ];
      const text = [button.dataset.intro, ...rows.filter(([, v]) => v).map(([label, v]) => `${label}: ${v}`)].join('\n');

      try {
        window.Shopify?.analytics?.publish?.('quote_whatsapp', { organisation_type: type || undefined });
        if (typeof window.gtag === 'function') window.gtag('event', 'quote_whatsapp', { organisation_type: type });
      } catch (error) {
        console.warn('KLHS quote analytics failed', error);
      }

      window.open(`https://wa.me/${button.dataset.number}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    }

    validStep(index) {
      const fields = [...this.steps[index].querySelectorAll('input, select, textarea')];
      const firstInvalid = fields.find((field) => !field.checkValidity());
      if (firstInvalid) {
        firstInvalid.reportValidity();
        return false;
      }
      return true;
    }

    go(index, validate) {
      if (index < 0 || index >= this.steps.length) return;
      if (validate && !this.validStep(this.current)) return;
      this.current = index;
      this.render();
      this.steps[index].querySelector('legend, input, select, textarea')?.focus?.();
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    render() {
      const stepped = this.media.matches;
      this.classList.toggle('klhs-steps--stepped', stepped);
      this.steps.forEach((step, index) => (step.hidden = stepped && index !== this.current));

      const label = this.querySelector('[data-step-label]');
      if (label) {
        const title = this.steps[this.current].dataset.stepTitle || '';
        label.textContent = `Step ${this.current + 1} of ${this.steps.length}${title ? ` · ${title}` : ''}`;
      }
      const bar = this.querySelector('[data-step-bar]');
      if (bar) bar.style.width = `${((this.current + 1) / this.steps.length) * 100}%`;

      this.querySelectorAll('[data-step-back]').forEach((button) => (button.hidden = !stepped || this.current === 0));
      this.querySelectorAll('[data-step-next]').forEach(
        (button) => (button.hidden = !stepped || this.current === this.steps.length - 1)
      );
      this.querySelectorAll('[data-step-submit]').forEach(
        (button) => (button.hidden = stepped && this.current !== this.steps.length - 1)
      );
    }
  }

  customElements.define('klhs-steps', KlhsSteps);
}

// <klhs-lightbox>: click a gallery photo to view it larger in a native <dialog>; arrows / swipe-free prev and next.
if (!customElements.get('klhs-lightbox')) {
  class KlhsLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.image = this.dialog.querySelector('img');
      this.items = [...this.querySelectorAll('[data-full]')];
      this.index = 0;

      this.items.forEach((item, index) => item.addEventListener('click', () => this.open(index)));
      this.dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.step(-1));
      this.dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => this.step(1));
      // Clicking the dimmed backdrop (the dialog element itself) closes it.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
      this.dialog.addEventListener('close', () => this.items[this.index]?.focus());
    }

    open(index) {
      this.index = index;
      this.render();
      this.dialog.showModal();
    }

    step(direction) {
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.render();
    }

    render() {
      const item = this.items[this.index];
      this.image.src = item.dataset.full;
      this.image.alt = item.dataset.alt || '';
    }
  }

  customElements.define('klhs-lightbox', KlhsLightbox);
}

// Open the FAQ answer a link points to (e.g. /pages/faq#faq-template--123__question_1).
(() => {
  const openFromHash = () => {
    const target = location.hash.length > 1 && document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (!target || !target.matches('details.klhs-faq__item')) return;
    target.open = true;
    target.scrollIntoView({ block: 'start' });
  };
  window.addEventListener('hashchange', openFromHash);
  openFromHash();
})();
